import { LogOut, Wifi, WifiOff, Bell, Menu, Layers } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth-store";
import { Button } from "@/components/ui/button";
import { api, apiUrl } from "@/lib/api-client";

const TEMPLATE_LABELS: Record<string, string> = {
  "villa-rental": "Villa Rental",
  restaurant: "Restaurant",
  spa: "Spa & Wellness",
  dentist: "Dentist",
  cafe: "Café",
};

interface HealthResponse {
  ok: boolean;
  service: string;
  env: string;
  db_ok: boolean;
  timestamp: string;
}

interface NotifRow {
  id: string;
}

interface Props {
  /** Called when the hamburger is clicked (mobile only). */
  onMobileMenuClick?: () => void;
}

export function TopBar({ onMobileMenuClick }: Props) {
  const clearToken = useAuth((s) => s.clearToken);

  const { data: health } = useQuery({
    queryKey: ["health"],
    queryFn: async (): Promise<HealthResponse> => {
      const res = await fetch(apiUrl("/api/health"));
      if (!res.ok) throw new Error("health unavailable");
      return res.json() as Promise<HealthResponse>;
    },
    refetchInterval: 30_000,
    retry: false,
  });

  const { data: unreadNotifs } = useQuery({
    queryKey: ["notifications-badge"],
    queryFn: () => api.get<NotifRow[]>(`/api/notifications?unread=true&limit=50`),
    refetchInterval: 20_000,
  });

  // Active vertical template (villa-rental / restaurant / spa / dentist /
  // cafe / unknown). Shown as a small badge so the operator knows at a
  // glance which "business" the bot is currently demoing.
  const { data: business, isLoading: businessLoading } = useQuery({
    queryKey: ["settings", "business"],
    queryFn: () =>
      api.get<{ current_template_slug: string | null }>(
        "/api/settings/business",
      ),
    staleTime: 30_000,
  });
  const activeSlug = business?.current_template_slug ?? null;
  // Distinguish "still loading" from "genuinely no template" so the badge
  // doesn't flash "No template" on every page load before snapping to the
  // real vertical.
  const activeTemplateLabel = businessLoading
    ? "…"
    : activeSlug
    ? TEMPLATE_LABELS[activeSlug] ?? activeSlug
    : "No template";

  const dbOk = Boolean(health?.db_ok);
  const env = health?.env ?? "?";
  // `?? []` (not destructuring default) so a null payload from a
  // briefly-empty API response doesn't blow up the entire shell.
  const unreadCount = unreadNotifs?.length ?? 0;

  return (
    <header className="flex h-12 shrink-0 items-center justify-between border-b border-slate-200 bg-brand-surface px-2 sm:px-4">
      <div className="flex items-center gap-2 sm:gap-3 text-xs text-slate-500 min-w-0">
        {/* Mobile hamburger */}
        <button
          type="button"
          onClick={onMobileMenuClick}
          className="md:hidden h-9 w-9 -ml-1 flex items-center justify-center text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" />
        </button>

        <span className="rounded bg-slate-100 px-2 py-0.5 font-mono uppercase tracking-wider hidden sm:inline">
          {env}
        </span>
        <span className="flex items-center gap-1.5">
          {dbOk ? (
            <Wifi className="h-3.5 w-3.5 text-emerald-600" />
          ) : (
            <WifiOff className="h-3.5 w-3.5 text-red-600" />
          )}
          <span className={`${dbOk ? "text-emerald-700" : "text-red-700"} hidden sm:inline`}>
            {dbOk ? "TiDB OK" : "TiDB down"}
          </span>
        </span>
        {/* Active vertical template - click to jump to the Templates page
            and switch. Mobile hides the label to save space, just shows
            the icon + slug. */}
        <Link
          to="/templates"
          className="inline-flex items-center gap-1.5 rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-100 transition-colors"
          title="Switch vertical template"
        >
          <Layers className="h-3.5 w-3.5 text-slate-500" />
          <span className="font-medium">{activeTemplateLabel}</span>
        </Link>
      </div>

      <div className="flex items-center gap-1 shrink-0">
        <kbd
          className="hidden lg:inline-flex items-center gap-1 text-[10px] text-slate-500 border border-slate-200 rounded px-1.5 py-0.5 mr-2"
          title="Open command palette"
        >
          <span>⌘</span><span>K</span>
        </kbd>
        <Link to="/notifications">
          <Button variant="ghost" size="sm" className="relative" title="Notifications">
            <Bell className="h-4 w-4" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </Button>
        </Link>
        <Button variant="ghost" size="sm" onClick={() => clearToken()} title="Log out">
          <LogOut className="h-4 w-4" />
          <span className="hidden sm:inline">Logout</span>
        </Button>
      </div>
    </header>
  );
}
